import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { canAccess, canEdit, canEditTimeline } from "../utils/permissions";
import {
  getAllTimelines,
  trackEditor,
  touchTimeline,
  updateAllowedEditors,
} from "../services/azureTimelinesService";
import { getAllProjectsWithTags } from "../services/azureProjectsService";
import { getAllHrSettings } from "../services/azureHrService";
import { TimelineItemsProvider } from "../services/timeline/timelineItemsProvider";
import TimelineEditorsModal from "../components/timeline/TimelineEditorsModal";
import { getPendenzen, updatePendenz } from "../services/azurePendenzenService";
import PendenzEditModal from "../components/PendenzEditModal";
import { richTextToPlainText } from "../utils/richText";

const EMBED_SRC = "/timeline-embed.html";
const SAVE_DEBOUNCE_MS = 700;

// --- Pendenzen als virtuelle Meilenstein-Gruppe im Terminplan ---
// Die Items werden dem iframe-State beigemischt (Rendering/Drag/Collapse gratis),
// aber vor dem Persistieren des Terminplans wieder herausgefiltert (Strip).
const PEND_GROUP_ID = "__pendenzen";
const pendTaskId = (id) => `__pend_${id}`;
const massgeblicherTermin = (p) => p.terminNeu || p.terminSoll || "";

const buildPendenzInjection = (state, pendenzen) => {
  const projectId =
    state.currentProjectId || (state.projects && state.projects[0] && state.projects[0].id);
  const dated = (pendenzen || [])
    .filter((p) => massgeblicherTermin(p))
    .sort((a, b) => massgeblicherTermin(a).localeCompare(massgeblicherTermin(b)));
  // Gruppe nur, wenn Projekt-Kontext UND mindestens eine datierte Pendenz vorhanden.
  if (!projectId || dated.length === 0) return { group: null, tasks: [] };
  const maxOrder = (state.groups || [])
    .filter((g) => g.projectId === projectId)
    .reduce((m, g) => Math.max(m, g.order || 0), -1);
  const group = {
    id: PEND_GROUP_ID,
    projectId,
    name: "Pendenzen",
    collapsed: true,
    order: maxOrder + 1,
    ownerId: null,
    parentId: null,
    dependsOn: [],
  };
  const tasks = dated.map((p, i) => ({
    id: pendTaskId(p.id),
    projectId,
    groupId: PEND_GROUP_ID,
    name: richTextToPlainText(p.beschreibung) || `Pendenz ${p.nummer || ""}`,
    isMilestone: true,
    dateMode: "day",
    startDate: massgeblicherTermin(p),
    durationDays: 0,
    done: p.status === "erledigt",
    dependsOn: [],
    order: i,
    ownerId: null,
    color: null,
    manualPosition: true,
  }));
  return { group, tasks };
};

const injectPendenzen = (state, pendenzen) => {
  if (!state) return state;
  const { group, tasks } = buildPendenzInjection(state, pendenzen);
  if (!group) return state;
  return {
    ...state,
    groups: [...(state.groups || []).filter((g) => g.id !== PEND_GROUP_ID), group],
    tasks: [
      ...(state.tasks || []).filter((t) => !(t.id || "").startsWith("__pend_")),
      ...tasks,
    ],
  };
};

const stripPendenzen = (state) => ({
  ...state,
  groups: (state.groups || []).filter((g) => g.id !== PEND_GROUP_ID),
  tasks: (state.tasks || []).filter((t) => !(t.id || "").startsWith("__pend_")),
});

const TimelineEditorWrapper = () => {
  const { timelineId } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  // GL/PL dürfen die Bearbeiter:innen-Freigabe pflegen.
  const canManageEditors = canEdit(user, "termine");

  const [timeline, setTimeline] = useState(null);
  const [linkedProject, setLinkedProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [saveStatus, setSaveStatus] = useState("idle"); // idle | saving | saved | error
  const [allowedEditors, setAllowedEditors] = useState([]);
  const [showEditorsModal, setShowEditorsModal] = useState(false);
  const [editingPendenz, setEditingPendenz] = useState(null);

  const pendenzenRef = useRef([]);
  const projectUuidRef = useRef(null);
  const projectCodeRef = useRef(null);
  const iframeRef = useRef(null);
  const providerRef = useRef(null);
  const prevStateRef = useRef(null); // zuletzt persistierter State (Basis für Diff)
  const loadPayloadRef = useRef(null); // State, der ans iframe geschickt werden soll
  const iframeReadyRef = useRef(false);
  const loadSentRef = useRef(false);
  const pendingStateRef = useRef(null);
  const saveTimerRef = useRef(null);
  const trackedRef = useRef(false);
  const userNameRef = useRef("");
  const readOnlyRef = useRef(true); // Spiegel von readOnly für stabile Callbacks
  const nonWorkingDaysRef = useRef([]); // arbeitsfreie Tage (Feiertage + Betriebsferien) für die Bridge

  if (!providerRef.current) providerRef.current = new TimelineItemsProvider();
  userNameRef.current = user?.userDetails || user?.email || user?.name || "";

  // readOnly REAKTIV aus aktuellem user + Freigabe ableiten und in den Ref spiegeln.
  // Wichtig: NICHT im Load-Effect einfrieren – sonst bliebe es bei einem Auth-Timing-
  // Problem fälschlich read-only (z.B. GL/PL wird beim ersten Render noch nicht erkannt,
  // weil user erst asynchron aus der Team-Tabelle geladen wird).
  const readOnly = timeline ? !canEditTimeline(user, { allowedEditors }) : true;
  readOnlyRef.current = readOnly;

  // --- Vollbild (wie Kosten-Editor: .content-area.fullscreen) ---
  const toggleFullscreen = useCallback(() => {
    setIsFullscreen((prev) => {
      const next = !prev;
      const contentArea = document.querySelector(".content-area");
      if (contentArea) contentArea.classList.toggle("fullscreen", next);
      return next;
    });
  }, []);

  useEffect(() => {
    return () => {
      const contentArea = document.querySelector(".content-area");
      if (contentArea) contentArea.classList.remove("fullscreen");
    };
  }, []);

  // Schickt den Initial-State ans iframe, sobald iframe bereit UND Payload vorhanden.
  const maybeSendLoad = useCallback(() => {
    if (loadSentRef.current) return;
    if (!iframeReadyRef.current || !loadPayloadRef.current) return;
    const frame = iframeRef.current;
    if (!frame || !frame.contentWindow) return;
    frame.contentWindow.postMessage(
      {
        type: "timeline:load",
        state: loadPayloadRef.current,
        readOnly: readOnlyRef.current,
        nonWorkingDays: nonWorkingDaysRef.current,
        projectCode: projectCodeRef.current || "", // für PDF-Dateiname {code}_TIME_{datum}_{name}
      },
      window.location.origin
    );
    loadSentRef.current = true;
  }, []);

  // --- Laden: Metadaten + Inhalt zusammensetzen ---
  useEffect(() => {
    if (authLoading) return; // erst laden, wenn Auth (user) settled ist
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setNotFound(false);
        const timelines = await getAllTimelines();
        const found = timelines.find((t) => t.id === timelineId);
        if (!found) {
          if (!cancelled) setNotFound(true);
          return;
        }
        if (cancelled) return;
        setTimeline(found);
        setAllowedEditors(found.allowedEditors || []);

        // Editierrecht für diesen Plan (für den initialen Settings-Write unten).
        // Das im UI/iframe genutzte readOnly wird zusätzlich reaktiv abgeleitet.
        const ro = !canEditTimeline(user, found);

        // Verknüpftes Projekt laden: Name + Projektfarbe aus der Projekterfassung (nicht kritisch).
        let projColor = null;
        if (found.linkedProjectCode) {
          try {
            const projects = await getAllProjectsWithTags("project");
            const proj = projects.find((p) => p.code === found.linkedProjectCode);
            if (proj) {
              projColor = proj.color || null;
              projectUuidRef.current = proj.id;
              projectCodeRef.current = proj.code;
              if (!cancelled) setLinkedProject({ code: proj.code, name: proj.title });
              // Pendenzen des verknüpften Projekts laden (für die virtuelle Gruppe).
              try {
                pendenzenRef.current = await getPendenzen(proj.id);
              } catch {
                pendenzenRef.current = [];
              }
            }
          } catch {
            /* ignore */
          }
        }

        // Arbeitsfreie Tage aus den HR-Settings (Wochenende rechnet das iframe selbst):
        // Feiertage + Betriebsferien über alle Jahre als ISO-Datumsstrings.
        try {
          const hr = await getAllHrSettings();
          const set = new Set();
          (hr || []).forEach((y) => {
            (y.betriebsferien || []).forEach((d) => {
              if (typeof d === "string") set.add(d.slice(0, 10));
            });
            (y.feiertage || []).forEach((f) => {
              const d = typeof f === "string" ? f : f && f.date;
              if (d) set.add(String(d).slice(0, 10));
            });
          });
          nonWorkingDaysRef.current = Array.from(set);
        } catch (e) {
          console.error("HR-Settings (Feiertage/Ferien) konnten nicht geladen werden:", e);
        }

        // Inhalt aus Entities zusammensetzen (oder Default-State für leeren Plan).
        const { state: initialState, hadSettings } = await providerRef.current.assemble(
          timelineId,
          found.name
        );
        if (cancelled) return;
        // Projektfarbe aus der Projekterfassung als Default-Balkenfarbe des Plans übernehmen.
        if (projColor && Array.isArray(initialState.projects)) {
          initialState.projects.forEach((p) => { p.color = projColor; });
        }
        prevStateRef.current = initialState;
        // Pendenzen-Gruppe beimischen (nur fürs iframe; prevStateRef bleibt "clean").
        loadPayloadRef.current = injectPendenzen(initialState, pendenzenRef.current);

        // Settings-Entity (stabile Projekt-ID) für neue Pläne einmalig anlegen, damit
        // Tasks nach Reload ihre Projekt-Zuordnung behalten. Nur, wenn editierberechtigt.
        if (!ro && !hadSettings) {
          try {
            await providerRef.current.upsertSettings(timelineId, initialState);
          } catch (e) {
            console.error("Initiales Settings-Speichern fehlgeschlagen:", e);
          }
        }

        maybeSendLoad();
      } catch (error) {
        console.error("Error loading timeline:", error);
        if (!cancelled) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timelineId, authLoading]);

  // --- Speichern (debounced Diff) ---
  const flushSave = useCallback(async () => {
    const next = pendingStateRef.current;
    if (!next || readOnlyRef.current) return;
    pendingStateRef.current = null;
    try {
      setSaveStatus("saving");
      await providerRef.current.persistDiff(timelineId, prevStateRef.current, next);
      prevStateRef.current = next;
      setSaveStatus("saved");
      // Metadaten-Zeitstempel + Editor-Tracking (Tracking nur einmal pro Sitzung).
      touchTimeline(timelineId);
      if (!trackedRef.current && userNameRef.current) {
        trackedRef.current = true;
        trackEditor(timelineId, userNameRef.current);
      }
    } catch (error) {
      console.error("Timeline save error:", error);
      setSaveStatus("error");
    }
  }, [timelineId]);

  const scheduleSave = useCallback(
    (state) => {
      if (readOnlyRef.current) return;
      pendingStateRef.current = state;
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => {
        saveTimerRef.current = null;
        flushSave();
      }, SAVE_DEBOUNCE_MS);
    },
    [flushSave]
  );

  // Datumsänderungen an Pendenzen-Meilensteinen (Drag im Gantt) in die Pendenz schreiben.
  const extractPendenzDateChanges = useCallback((state) => {
    const uuid = projectUuidRef.current;
    if (!uuid || !state) return;
    (state.tasks || []).forEach((t) => {
      const tid = t.id || "";
      if (!tid.startsWith("__pend_")) return;
      const pid = tid.slice(7);
      const p = pendenzenRef.current.find((x) => x.id === pid);
      if (!p) return;
      const newDate = t.startDate || "";
      const cur = massgeblicherTermin(p);
      if (newDate && newDate !== cur) {
        const field = p.terminNeu ? "terminNeu" : "terminSoll";
        const updated = { ...p, [field]: newDate };
        pendenzenRef.current = pendenzenRef.current.map((x) =>
          x.id === pid ? updated : x
        );
        updatePendenz(uuid, pid, updated).catch((e) =>
          console.error("Pendenz-Datum konnte nicht gespeichert werden:", e)
        );
      }
    });
  }, []);

  // Nach einer Änderung im Pendenzen-Modal die Gruppe neu einspeisen (iframe neu laden).
  const reinjectPendenzen = useCallback(() => {
    const base = pendingStateRef.current || prevStateRef.current;
    if (!base) return;
    loadPayloadRef.current = injectPendenzen(base, pendenzenRef.current);
    loadSentRef.current = false;
    maybeSendLoad();
  }, [maybeSendLoad]);

  // --- postMessage-Bridge ---
  useEffect(() => {
    const handleMessage = (event) => {
      // Sicherheit: nur Nachrichten vom eigenen Origin und aus unserem iframe akzeptieren.
      if (event.origin !== window.location.origin) return;
      if (iframeRef.current && event.source !== iframeRef.current.contentWindow) return;
      const data = event.data;
      if (!data || typeof data !== "object") return;

      if (data.type === "timeline:ready") {
        iframeReadyRef.current = true;
        maybeSendLoad();
      } else if (data.type === "timeline:save") {
        if (data.state) {
          // Pendenzen-Datumsänderungen extrahieren, dann die virtuelle Gruppe wieder
          // herausfiltern, damit sie den Terminplan nicht verändert.
          extractPendenzDateChanges(data.state);
          scheduleSave(stripPendenzen(data.state));
        }
      } else if (data.type === "timeline:pendenzClicked") {
        const p = pendenzenRef.current.find((x) => x.id === data.pendenzId);
        if (p) setEditingPendenz(p);
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [maybeSendLoad, scheduleSave, extractPendenzDateChanges]);

  // Letzten ausstehenden Stand beim Verlassen noch wegschreiben (best effort).
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      if (pendingStateRef.current && !readOnlyRef.current) {
        const next = pendingStateRef.current;
        pendingStateRef.current = null;
        providerRef.current
          .persistDiff(timelineId, prevStateRef.current, next)
          .catch((e) => console.error("Finaler Save beim Verlassen fehlgeschlagen:", e));
      }
    };
  }, [timelineId]);

  // Routing-Guards
  useEffect(() => {
    if (notFound) navigate("/termine");
  }, [notFound, navigate]);

  // Bearbeiter:innen-Freigabe speichern (GL/PL).
  const handleSaveEditors = useCallback(
    async (list) => {
      await updateAllowedEditors(timelineId, list);
      setAllowedEditors(list); // readOnly leitet sich reaktiv aus allowedEditors neu ab
      setShowEditorsModal(false);
    },
    [timelineId]
  );

  if (authLoading || loading) {
    return <div className="loading">Terminplan wird geladen...</div>;
  }

  if (!user || !canAccess(user, "termine")) {
    return (
      <div className="access-denied">
        <h3>Zugriff verweigert</h3>
        <p>Sie haben keine Berechtigung für diesen Bereich.</p>
      </div>
    );
  }

  if (!timeline) return null;

  const saveLabel =
    saveStatus === "saving"
      ? "Speichert…"
      : saveStatus === "saved"
      ? "Gespeichert"
      : saveStatus === "error"
      ? "Fehler beim Speichern"
      : "";

  return (
    <div className="timeline-editor-layout">
      <div className="timeline-editor-bar">
        <button className="btn btn-secondary btn-sm" onClick={() => navigate("/termine")}>
          ← Zurück
        </button>
        <span className="timeline-editor-title">{timeline.name}</span>
        {linkedProject && (
          <span className="timeline-editor-project">
            {linkedProject.code} – {linkedProject.name}
          </span>
        )}
        {readOnly && <span className="timeline-readonly-badge">Nur Lesen</span>}
        <span className="timeline-editor-spacer" />
        <span className="timeline-save-status">{saveLabel}</span>
        {canManageEditors && (
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => setShowEditorsModal(true)}
            title="Festlegen, wer diesen Terminplan bearbeiten darf"
          >
            Bearbeiter:innen
            {allowedEditors.length > 0 ? ` (${allowedEditors.length})` : ""}
          </button>
        )}
        <button className="btn btn-secondary btn-sm" onClick={toggleFullscreen}>
          {isFullscreen ? "Vollbild verlassen" : "Vollbild"}
        </button>
      </div>
      <iframe
        ref={iframeRef}
        className="timeline-embed-frame"
        src={EMBED_SRC}
        title={`Terminplan: ${timeline.name}`}
        onLoad={() => {
          // Fallback: falls die "ready"-Nachricht verpasst wurde, beim Load erneut prüfen.
          iframeReadyRef.current = true;
          maybeSendLoad();
        }}
      />

      {showEditorsModal && (
        <TimelineEditorsModal
          timelineName={timeline.name}
          currentEditors={allowedEditors}
          onSave={handleSaveEditors}
          onClose={() => setShowEditorsModal(false)}
        />
      )}

      {editingPendenz && (
        <PendenzEditModal
          pendenz={editingPendenz}
          projectUuid={projectUuidRef.current}
          projectCode={projectCodeRef.current}
          onClose={() => setEditingPendenz(null)}
          onSaved={(updated) => {
            pendenzenRef.current = pendenzenRef.current.map((x) =>
              x.id === updated.id ? updated : x
            );
            setEditingPendenz(null);
            reinjectPendenzen();
          }}
          onDeleted={(id) => {
            pendenzenRef.current = pendenzenRef.current.filter((x) => x.id !== id);
            setEditingPendenz(null);
            reinjectPendenzen();
          }}
        />
      )}
    </div>
  );
};

export default TimelineEditorWrapper;
